import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Login.scss";
import { setAuthToken } from "../../../utils/api";
import {
  fetchClientIp,
  postUserLogin,
  postInstantDemo,
  buildAppSessionFromLoginResponse,
  buildPasswordLoginBody,
  buildInstantDemoBody,
  mapAxiosErrorToMessage,
  SERVER_LIVE,
} from "../../../services/authService";
import { errorLog } from "../../../utils/logger";
import { TRADE_BAZAAR_COPYRIGHT, PRIVACY_POLICY_PATH } from "../../../constants/legalContent";

/**
 * Main login / Instant Demo screen.
 * Auth wire-up lives in services/authService.js — this file is UI + sessionStorage only.
 */

const SHOW_OPEN_DEMO_UI = false;

function LoginTabIcon({ type }) {
  if (type === "signin") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"
          stroke="#2196f3"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"
          stroke="#2196f3"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#4caf50" aria-hidden>
      <circle cx="10" cy="8" r="3.2" />
      <path d="M4 19c0-3.1 2.7-5.4 6-5.4s6 2.3 6 5.4" />
      <path d="M16 8h4M18 6v4" stroke="#4caf50" strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function EyeIcon({ open }) {
  if (open) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
          stroke="currentColor"
          strokeWidth="2"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-10-8-10-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 10 8 10 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 10 8 10 8a18.5 18.5 0 01-2.16 3.19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const Login = ({ onLogin, isAdminLogin = false }) => {
  const [view, setView] = useState("connect");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [instantLoading, setInstantLoading] = useState(false);
  const [error, setError] = useState("");

  const [demoForm, setDemoForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    hedge: true,
    accountType: "Forex Hedged USD",
    deposit: "100000",
    leverage: "100",
    agreed: false,
  });

  useEffect(() => {
    fetchClientIp();
  }, []);

  useEffect(() => {
    if (!SHOW_OPEN_DEMO_UI && view === "demo") setView("connect");
  }, [view]);

  const persistSessionAndNotify = (sessionData) => {
    const storageKey = isAdminLogin ? "fxbazzar_admin" : "fxbazzar_user";
    sessionStorage.setItem(storageKey, JSON.stringify(sessionData));
    // Keep legacy encoded session in sync; many screens read TradeSession first.
    sessionStorage.setItem("TradeSession", window.btoa(JSON.stringify(sessionData)));
    setAuthToken(sessionData.token);
    onLogin(sessionData);
  };

  const handleConnect = async () => {
    if (!login || !password) {
      setError("Please enter login and password");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const userIp = await fetchClientIp();
      const body = buildPasswordLoginBody({
        username: login.trim(),
        password,
        userIp,
        isUserLogin: !isAdminLogin,
      });
      const data = await postUserLogin(body);
      const sessionData = buildAppSessionFromLoginResponse(data, {
        displayLogin: login.trim(),
        isDemoAccount: false,
        isAdminLogin,
        userIpForSession: userIp,
      });
      persistSessionAndNotify(sessionData);
    } catch (err) {
      errorLog("Login error:", err?.message);
      setError(mapAxiosErrorToMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInstantDemo = async () => {
    setInstantLoading(true);
    setError("");
    try {
      const userIp = await fetchClientIp();
      const body = buildInstantDemoBody({ userIp, isUserLogin: true });
      const data = await postInstantDemo(body);
      const sessionData = buildAppSessionFromLoginResponse(data, {
        displayLogin: "demo",
        isDemoAccount: true,
        isAdminLogin,
        userIpForSession: userIp,
      });
      persistSessionAndNotify(sessionData);
    } catch (err) {
      errorLog("InstantDemo error:", err?.message);
      setError(mapAxiosErrorToMessage(err));
    } finally {
      setInstantLoading(false);
    }
  };

  const setDemo = (k, v) => setDemoForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="login-container login-container--fullscreen login-container--mt5">
      <div className="login-mt5">
        <div className="login-mt5__header">
          <span className="login-mt5__title">
            {isAdminLogin ? "Admin Login" : "Trading accounts: FxBazzar Ltd."}
          </span>
        </div>

        <div className="login-mt5__body">
          <aside className="login-mt5__sidebar">
            {!isAdminLogin && (
              <div className="login-tabs" role="tablist" aria-label="Login mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "connect"}
                  className={`login-tab${view === "connect" ? " login-tab--active" : ""}`}
                  onClick={() => setView("connect")}
                >
                  <LoginTabIcon type="signin" />
                  <span className="login-tab__text">
                    <span className="login-tab__label">Sign in</span>
                    <span className="login-tab__sub">Real account</span>
                  </span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "instant"}
                  className={`login-tab${view === "instant" ? " login-tab--active" : ""}`}
                  onClick={() => setView("instant")}
                >
                  <LoginTabIcon type="demo" />
                  <span className="login-tab__text">
                    <span className="login-tab__label">Instant Demo</span>
                    <span className="login-tab__sub">No password</span>
                  </span>
                </button>
              </div>
            )}
            {!isAdminLogin && (
              <ul className="login-brand__highlights">
                <li>Live quotes &amp; market watch</li>
                <li>Charts, orders &amp; positions</li>
                <li>Secure account access</li>
              </ul>
            )}
            <div className="login-footer">
              <p>{TRADE_BAZAAR_COPYRIGHT}</p>
              <Link to={PRIVACY_POLICY_PATH}>Privacy Policy</Link>
            </div>
          </aside>

          <div className="login-panel">
        <div className="login-form">
          {view === "connect" && (
            <>
              <h3 className="login-form__heading">
                {isAdminLogin ? "Admin credentials" : "Welcome back"}
              </h3>
              {!isAdminLogin && (
                <p className="login-form__hint">
                  Use your real trading login and password to connect.
                </p>
              )}
              <div className="form-group">
                <label htmlFor="login-username">Login</label>
                <input
                  id="login-username"
                  type="text"
                  placeholder="Enter login"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleConnect()}
                  autoComplete="username"
                />
              </div>
              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <div className="password-wrapper">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleConnect()}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="eye-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
              </div>
              {isAdminLogin && (
                <div className="form-group">
                  <label htmlFor="login-server">Server</label>
                  <input
                    id="login-server"
                    type="text"
                    value={SERVER_LIVE}
                    disabled
                    className="input--disabled"
                  />
                </div>
              )}
              {error && <div className="error-message">{error}</div>}
              <button
                type="button"
                className="connect-btn"
                onClick={handleConnect}
                disabled={loading || instantLoading}
              >
                {loading ? "Connecting…" : "Connect to account"}
              </button>
            </>
          )}

          {!isAdminLogin && view === "instant" && (
            <>
              <h3 className="login-form__heading">Try instant demo</h3>
              <p className="login-form__hint login-form__hint--instant">
                A shared demo account for training — no login or password needed. No real money
                is involved.
              </p>
              <div className="login-demo-features">
                <div className="login-demo-feature">
                  <span className="login-demo-feature__dot" aria-hidden />
                  Live market data &amp; charts
                </div>
                <div className="login-demo-feature">
                  <span className="login-demo-feature__dot" aria-hidden />
                  Practice orders risk-free
                </div>
                <div className="login-demo-feature">
                  <span className="login-demo-feature__dot" aria-hidden />
                  One tap to start
                </div>
              </div>
              {error && <div className="error-message">{error}</div>}
              <button
                type="button"
                className="connect-btn connect-btn--instant"
                onClick={handleInstantDemo}
                disabled={loading || instantLoading}
              >
                {instantLoading ? "Starting instant demo…" : "Start Instant Demo"}
              </button>
            </>
          )}

          {SHOW_OPEN_DEMO_UI && view === "demo" && (
            <>
              <h3 className="login-form__heading">Open Demo account</h3>
              <div className="demo-company">
                <span className="demo-company__label">Company</span>
                <span className="demo-company__name">FxBazzar Ltd.</span>
              </div>
              <div className="form-group">
                <label>Your Name</label>
                <div className="form-row-2">
                  <input
                    type="text"
                    placeholder="Name"
                    value={demoForm.firstName}
                    onChange={(e) => setDemo("firstName", e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Surname"
                    value={demoForm.lastName}
                    onChange={(e) => setDemo("lastName", e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Email"
                  value={demoForm.email}
                  onChange={(e) => setDemo("email", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Mobile phone</label>
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={demoForm.phone}
                  onChange={(e) => setDemo("phone", e.target.value)}
                />
              </div>
              <div className="form-group form-group--inline">
                <input
                  type="checkbox"
                  id="hedge"
                  checked={demoForm.hedge}
                  onChange={(e) => setDemo("hedge", e.target.checked)}
                />
                <label htmlFor="hedge">Use hedge in trading</label>
              </div>
              <div className="form-group">
                <label>Account type</label>
                <select value={demoForm.accountType} onChange={(e) => setDemo("accountType", e.target.value)}>
                  <option>Forex Hedged USD</option>
                  <option>Forex Standard USD</option>
                  <option>Crypto USD</option>
                </select>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Deposit</label>
                  <select value={demoForm.deposit} onChange={(e) => setDemo("deposit", e.target.value)}>
                    {["10000", "25000", "50000", "100000", "200000"].map((v) => (
                      <option key={v} value={v}>
                        {parseInt(v, 10).toLocaleString()} USD
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group form-group--narrow">
                  <label>Leverage</label>
                  <select value={demoForm.leverage} onChange={(e) => setDemo("leverage", e.target.value)}>
                    {["50", "100", "200", "500"].map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group form-group--inline form-group--terms">
                <input
                  type="checkbox"
                  id="terms"
                  checked={demoForm.agreed}
                  onChange={(e) => setDemo("agreed", e.target.checked)}
                />
                <label htmlFor="terms">
                  I agree with the{" "}
                  <a href="#terms">terms and conditions</a> for opening an account and the data
                  protection policy
                </label>
              </div>
              <div className="login-form__link-row">
                <a href="https://fxbazzar.com" target="_blank" rel="noreferrer">
                  www.fxbazzar.com
                </a>
              </div>
              <button className="connect-btn" type="button" disabled>
                Open Demo account
              </button>
            </>
          )}
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
